import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getCompanyTabs } from '@components/company-tabs/utils/get-company-tabs';
import { Tab } from '@components/tabs/tabs.type';
import {
  ENABLED_COMPANY_EVENT_DEFAULTS_ROUTE,
  ENABLED_COMPANY_INFO_ROUTE,
  ENABLED_COMPANY_WIDGET_ROUTE,
  ENABLED_USERS_ROUTE,
} from '@constants/company-auth-permissions';
import { CompanyClubContext } from '@contexts/company-club/company-club-context';
import { useIsProtectedElement } from '@hooks/use-is-protected-element';

export const useCompanyTabs = (): { tabs: Tab[] } => {
  const { t } = useTranslation();
  const { companyId, clubSlug } = useParams<{ companyId: string; clubSlug: string }>();
  const { isDelayedDeliveryActive, isResaleSettingsActive, isVenueSettingsActive } = useContext(CompanyClubContext);

  const isEnabledCompanyInfo = useIsProtectedElement({
    permissions: ENABLED_COMPANY_INFO_ROUTE,
    checkEveryPermission: false,
  });
  const isEnabledUsers = useIsProtectedElement({ permissions: ENABLED_USERS_ROUTE });
  const hasWidgetPermission = useIsProtectedElement({ permissions: ENABLED_COMPANY_WIDGET_ROUTE });
  const hasEventDefaultsPermission = useIsProtectedElement({ permissions: ENABLED_COMPANY_EVENT_DEFAULTS_ROUTE });

  const tabs = getCompanyTabs({
    companyId: companyId as string,
    clubSlug: clubSlug as string,
    isEnabledCompanyInfo,
    // The tab hosts one section per flag, so it appears as soon as either has something to show.
    isEnabledEventDefaults: (isDelayedDeliveryActive || isResaleSettingsActive) && hasEventDefaultsPermission,
    isEnabledUsers,
    isEnabledWidget: hasWidgetPermission,
    isVenueSettingsActive,
    t,
  });

  return { tabs };
};
